import {
  FederalTransferQualification,
  FEDERAL_TRANSFER_QUALIFICATION_OPTIONS,
} from '@signalvote/shared';

// #711 — the question that decides whether a federal transfer is exempt from the contribution
// limits, and the copy that explains the answer.
//
// The exempt set is defined by the RELATIONSHIP between the two committees (11 CFR 110.3(c)
// "Permissible Transfers"), never by the giver merely being a committee. Before this, picking the
// "Federal Transfer" label was by itself enough to clear every ceiling AND claim Schedule A line
// 12, with nothing checking a relationship existed.
//
// "None of these" is a real answer, not a missing one: the receipt is an ordinary committee gift,
// meters like any other, and files on the contributor's own line inside total contributions. It is
// also the default, because silence must never buy the exemption.
//
// Shared by the two entry surfaces (the Add-Contribution dialog and the bank-transaction classify
// panel) and the triage ledger, so their wording and behavior cannot drift.

/**
 * The one-line summary of how a recorded transfer was answered and what follows from it, shown on
 * the contribution itself so a treasurer never has to infer why one transfer metered and another
 * did not.
 *
 * It lives HERE, in the federal-only module, rather than inline in the shared triage ledger: the
 * wording is correctly FEC-framed (Schedule A line 12 is the FEC's own line for this receipt), and
 * the state-filer copy discipline keeps that vocabulary out of jurisdiction-shared surfaces. Its
 * callers must gate on the jurisdiction declaring the receipt — Alabama meters this same receipt
 * nature at $1,000 and Maryland maps it to its own transfer category, so telling either that the
 * row is "exempt from the contribution limits" would be false.
 */
export function transferQualificationSummary(
  qualification: FederalTransferQualification | null | undefined,
): string {
  switch (qualification) {
    case FederalTransferQualification.SameCandidateAuthorizedCommittee:
      return 'From another committee authorized by the same candidate — exempt from the '
        + 'contribution limits, files as a transfer on Schedule A line 12.';
    case FederalTransferQualification.JointFundraisingProceeds:
      return 'This committee’s share of a joint fundraiser — exempt from the contribution limits, '
        + 'files as a transfer on Schedule A line 12.';
    case FederalTransferQualification.None:
      return 'No qualifying relationship recorded — counted toward the contribution limits and '
        + 'filed as an ordinary contribution.';
    case null:
    case undefined:
    default:
      return 'Recorded before this app asked what makes a transfer exempt. It still files as a '
        + 'transfer; confirm what makes it exempt.';
  }
}

export interface TransferQualificationFieldProps {
  /** The recorded relationship. `null` only for a row that predates the gate. */
  qualification: FederalTransferQualification | null;
  onChange: (qualification: FederalTransferQualification) => void;
  /** Distinguishes the two entry surfaces so their inputs get unique ids. */
  idPrefix?: string;
}

export function TransferQualificationField({
  qualification,
  onChange,
  idPrefix = 'transfer-qualification',
}: TransferQualificationFieldProps) {
  // A legacy row (null) shows the unanswered prompt rather than silently reading as "None" —
  // the two mean different things, and only one of them still carries the exemption.
  const value = qualification ?? '';

  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <label htmlFor={idPrefix} className="block text-sm font-medium mb-1">
        What makes this transfer exempt? *
      </label>
      <select
        id={idPrefix}
        data-testid="transfer-qualification"
        value={value}
        onChange={(e) => onChange(e.target.value as FederalTransferQualification)}
        className="w-full rounded-md border px-3 py-2 text-sm"
      >
        {value === '' && <option value="">Select…</option>}
        {FEDERAL_TRANSFER_QUALIFICATION_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <p className="mt-2 text-xs text-muted-foreground">
        {transferQualificationSummary(qualification)}
      </p>
    </div>
  );
}
